package com.mencanhealth.admin.ui

import androidx.compose.runtime.*
import com.mencanhealth.admin.data.AdminAuthService
import com.mencanhealth.admin.data.AdminChatService
import com.mencanhealth.admin.data.AdminFirestoreService
import com.mencanhealth.admin.ui.components.Sidebar
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.*

enum class Page {
    USERS, USER_DETAIL, CHAT, ANALYTICS, DELETION_REQUESTS,
    APPOINTMENTS, AVAILABILITY
}

@Composable
fun AdminApp() {
    val currentPath = window.location.pathname

    when {
        currentPath == "/" || currentPath == "" -> {
            LandingPage(
                onNavigateToBooking = {
                    window.location.href = "/agendar"
                }
            )
        }
        currentPath == "/agendar" -> {
            BookingPage(
                onNavigateHome = {
                    window.location.href = "/"
                }
            )
        }
        currentPath.startsWith("/admin") -> {
            AdminPanel()
        }
        else -> {
            LandingPage(
                onNavigateToBooking = {
                    window.location.href = "/agendar"
                }
            )
        }
    }
}

@Composable
private fun AdminPanel() {
    val authService = remember { AdminAuthService() }
    val firestoreService = remember { AdminFirestoreService() }
    val chatService = remember { AdminChatService() }
    val authState by authService.state.collectAsState()
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        authService.checkExistingSession()
    }

    if (!authState.isAuthenticated) {
        LoginPage(
            isLoading = authState.isLoading,
            error = authState.error,
            onSignIn = { email, password ->
                scope.launch { authService.signIn(email, password) }
            }
        )
    } else {
        val initialPage = when (window.location.search.removePrefix("?").split("&").associate {
            val parts = it.split("=", limit = 2)
            parts.getOrElse(0) { "" } to parts.getOrElse(1) { "" }
        }["page"]) {
            "chat" -> Page.CHAT
            "users" -> Page.USERS
            "deletions" -> Page.DELETION_REQUESTS
            "analytics" -> Page.ANALYTICS
            else -> Page.USERS
        }
        var currentPage by remember { mutableStateOf(initialPage) }
        var selectedUserId by remember { mutableStateOf<String?>(null) }

        Div({ classes("dashboard") }) {
            Sidebar(
                currentPage = currentPage,
                email = authState.email ?: "",
                onNavigate = { currentPage = it; selectedUserId = null },
                onSignOut = { scope.launch { authService.signOut() } }
            )
            Div({ classes("main-content") }) {
                when (currentPage) {
                    Page.USERS -> UsersPage(
                        firestoreService = firestoreService,
                        onSelectUser = { uid ->
                            selectedUserId = uid
                            currentPage = Page.USER_DETAIL
                        }
                    )
                    Page.USER_DETAIL -> UserDetailPage(
                        firestoreService = firestoreService,
                        userId = selectedUserId ?: "",
                        onBack = { currentPage = Page.USERS }
                    )
                    Page.CHAT -> ChatPage(chatService = chatService)
                    Page.ANALYTICS -> AnalyticsPage(firestoreService = firestoreService)
                    Page.DELETION_REQUESTS -> DeletionRequestsPage(firestoreService = firestoreService)
                    Page.APPOINTMENTS -> AppointmentManagementPage()
                    Page.AVAILABILITY -> AvailabilityManagementPage()
                }
            }
        }
    }
}
